// Funcionalidades (features) de los comercios: tipos y formato compartidos por el registro autogestionado,
// "Mis funcionalidades" y la consulta de comercios de los bancos.

export interface CatalogFeature {
    id: number;
    code: string;
    name: string;
    description: string | null;
    base: boolean;              // incluida siempre (retail)
    order: number;
    monthlyPrice: number | null;
    currency: string | null;    // CRC / USD
    active?: boolean;           // "Mis funcionalidades": ya la tiene el comercio
    perUser?: boolean;          // el precio es por usuario (retail, inventario)
}

export interface MerchantFeature {
    code: string;
    name: string;
    monthlyPrice: number | null;
    currency: string | null;
    origin?: string;
    activatedAt?: string | null;
    cancelledAt?: string | null;    // el comercio la canceló
    activeUntil?: string | null;    // 'YYYY-MM-DD': la puede usar hasta ese día (fin del mes en que canceló)
    // Facturación electrónica y contabilidad: se cobran por bloque de uso del mes (monthlyPrice es por bloque)
    usage?: number;                 // documentos emitidos / facturas leídas en el mes
    blockSize?: number;
    blocks?: number;
    amount?: number | null;         // lo que paga este mes: monthlyPrice × bloques
}

export interface MonthlyTotal { currency: string; amount: number; }

export interface MerchantWithFeatures {
    id: number;
    name: string;
    typeLegalId: number | null;
    documentNumber: string | null;
    status: string;
    bankId: number | null;
    countryId: number | null;
    parent: { id: number; name: string | null } | null;
    createdAt: string | null;
    features: MerchantFeature[];
    monthlyTotal: MonthlyTotal[];
}

export const FEATURE_ICON: Record<string, string> = {
    'retail': 'bi-shop',
    'inventory': 'bi-box-seam',
    'electronic-invoice': 'bi-receipt',
    'accounting': 'bi-journal-text',
    'loyalty': 'bi-stars',
    'payment-link': 'bi-link-45deg',
};

export const featureIcon = (code: string) => FEATURE_ICON[code] ?? 'bi-puzzle';

export const ORIGIN_LABEL: Record<string, string> = {
    AUTOGESTION: 'Autogestión',
    API: 'API del banco',
    COMERCIO: 'Agregada por el comercio',
    MIGRACION: 'Migración',
};

// 'YYYY-MM-DD' como fecha local (sin el corrimiento de zona horaria de new Date('YYYY-MM-DD'))
export const formatDay = (day: string | null | undefined) => {
    const [y, m, d] = String(day ?? '').split('-').map(Number);
    return y && m && d ? new Date(y, m - 1, d).toLocaleDateString('es-CR', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
};

export const formatMoney = (amount: number | null | undefined, currency: string | null | undefined) => {
    if (amount === null || amount === undefined || !currency) return 'Precio por definir';
    try {
        return new Intl.NumberFormat('es-CR', { style: 'currency', currency, maximumFractionDigits: currency === 'CRC' ? 0 : 2 }).format(amount);
    } catch {
        return `${currency} ${amount}`;
    }
};

// Total mensual de varias funcionalidades, por moneda
export const totalOf = (features: { monthlyPrice: number | null; currency: string | null }[]): MonthlyTotal[] => {
    const totals: Record<string, number> = {};
    features.forEach(f => {
        if (f.monthlyPrice === null || !f.currency) return;
        totals[f.currency] = (totals[f.currency] ?? 0) + Number(f.monthlyPrice);
    });
    return Object.entries(totals).map(([currency, amount]) => ({ currency, amount }));
};

export const formatTotals = (totals: MonthlyTotal[]) =>
    totals.length ? totals.map(t => formatMoney(t.amount, t.currency)).join(' + ') : formatMoney(null, null);

// Precio mensual de una funcionalidad para la cantidad de usuarios contratada (las por usuario se multiplican)
export const priceFor = (feature: { monthlyPrice: number | null; perUser?: boolean }, users: number) =>
    feature.monthlyPrice === null ? null : Math.round(Number(feature.monthlyPrice) * (feature.perUser ? users : 1) * 100) / 100;

// Total mensual con la cantidad de usuarios
export const planTotalOf = (features: { monthlyPrice: number | null; currency: string | null; perUser?: boolean }[], users: number): MonthlyTotal[] =>
    totalOf(features.map(f => ({ monthlyPrice: priceFor(f, users), currency: f.currency })));

// ------------------------------------------------------------------ Tarjeta (registro y cuenta suspendida)

export const MAX_USERS = 100;

export interface CardForm { number: string; expiration: string; cvv: string; name: string; }

export const emptyCard: CardForm = { number: '', expiration: '', cvv: '', name: '' };

// '4111111111111111' → '4111 1111 1111 1111'
export const formatCardNumber = (value: string) => value.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');

// '1228' → '12/28'
export const formatExpiration = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

const luhn = (digits: string) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
        let n = Number(digits[digits.length - 1 - i]);
        if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
        sum += n;
    }
    return sum % 10 === 0;
};

export const cardErrors = (card: CardForm, now = new Date()): Record<string, string> => {
    const errors: Record<string, string> = {};
    const number = card.number.replace(/\D/g, '');
    if (!/^\d{13,19}$/.test(number) || !luhn(number)) errors.cardNumber = 'Ingrese un número de tarjeta válido.';
    const exp = card.expiration.replace(/\D/g, '');
    if (!/^(0[1-9]|1[0-2])\d{2}$/.test(exp)) errors.cardExpiration = 'Use el formato MM/AA.';
    else {
        const month = Number(exp.slice(0, 2));
        const year = 2000 + Number(exp.slice(2));
        if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) errors.cardExpiration = 'La tarjeta está vencida.';
    }
    if (!/^\d{3,4}$/.test(card.cvv)) errors.cardCvv = '3 o 4 dígitos.';
    if (!card.name.trim()) errors.cardName = 'Ingrese el nombre como aparece en la tarjeta.';
    return errors;
};

export const cardPayload = (card: CardForm) => ({
    number: card.number.replace(/\D/g, ''),
    expiration: card.expiration.replace(/\D/g, ''),
    cvv: card.cvv,
    name: card.name.trim(),
});

export const cardBrandIcon = (brand?: string | null) =>
    brand === 'VISA' ? 'bi-credit-card-2-front' : brand === 'MASTERCARD' ? 'bi-credit-card-fill' : 'bi-credit-card';
